import { useQuery } from "@tanstack/react-query"
import { CircleAlert, Lock, Printer } from "lucide-react"
import { useParams } from "react-router"

import { api } from "@/api/cliente"
import { type Gerencial, VistaGerencial } from "@/componentes/administracion/VistaGerencial"
import { type Facturacion, VistaFacturacion } from "@/componentes/resumen/Facturacion"
import type { Resumen } from "@/componentes/resumen/tipos"
import { VistaResumen } from "@/componentes/resumen/VistaResumen"
import { Boton } from "@/componentes/ui/Boton"
import { comoFechaHora } from "@/lib/fechas"

type Foto = {
  tipo: "ALCANCE" | "FACTURACION" | "GERENCIAL"
  titulo: string
  generado_en: string
  expira_en: string
  empresa_nombre: string
  empresa_rif: string
  alcance: Resumen | null
  facturacion: Facturacion | null
  /** El resumen gerencial de los viernes (28/10). */
  gerencial: Gerencial | null
}

/**
 * La FOTO de un reporte del Resumen Macro desde el enlace del aviso (`/m/<token>`, RM-6/RM-8,
 * 23/10): el alcance de los jueves o la facturación de los viernes. **Sin sesión, sin menú y
 * sin poder tocar nada**; pinta con los MISMOS componentes que el panel («mismo formato bot y
 * sistema») y su PDF es la impresión del navegador.
 */
export function MacroPublica() {
  const { token = "" } = useParams()
  const consulta = useQuery({
    queryKey: ["publico", "macro", token],
    queryFn: () => api.get<Foto>(`/api/v1/publico/macro/${token}`),
    retry: false,
  })

  if (consulta.isPending) {
    return (
      <p role="status" className="p-10 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }
  if (consulta.isError || !consulta.data) {
    return (
      <div className="mx-auto max-w-lg p-10 text-center">
        <CircleAlert size={28} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
        <h1 className="mt-3 text-lg font-semibold">Este enlace ya no vale</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Puede haber caducado. El próximo aviso trae el reporte nuevo.
        </p>
      </div>
    )
  }
  const f = consulta.data
  return (
    <main className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            {f.empresa_nombre}
            {f.empresa_rif ? ` · ${f.empresa_rif}` : ""}
          </p>
          <h1 className="text-xl font-semibold">{f.titulo}</h1>
          <p className="flex items-center gap-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            <Lock size={12} aria-hidden={true} /> Foto de sólo lectura del{" "}
            {comoFechaHora(f.generado_en)} · el enlace vale hasta el {comoFechaHora(f.expira_en)}
          </p>
        </div>
        <Boton className="no-print" onClick={() => window.print()}>
          <Printer size={16} aria-hidden={true} /> Imprimir / PDF
        </Boton>
      </header>
      {f.alcance && <VistaResumen r={f.alcance} soloLectura={true} />}
      {f.facturacion && <VistaFacturacion f={f.facturacion} />}
      {f.gerencial && <VistaGerencial g={f.gerencial} />}
    </main>
  )
}
